import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth } from '../utils/api';
import ToothSVG, { MissingTooth, ImplantTooth } from './ToothSVG';
import {
  Smile, Save, RotateCcw, History, X,
  Link as LinkIcon, Layers, Calendar
} from 'lucide-react';

// Format date for display (inline helper)
const formatDate = (date, locale = 'th-TH') => {
  if (!date) return '';
  return new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// A stable, order-independent fingerprint of the chart's clinical content.
// Only the fields Save actually writes are included, so re-fetching the same
// chart (which returns extra columns such as odonto_id and recorded_date, and
// drops the client-only `isNew` flag) never reads as an edit.
const chartFingerprint = (data) => JSON.stringify(
  Object.keys(data || {}).sort().map((tooth) => [
    tooth,
    Object.keys(data[tooth] || {}).sort().map((surface) => {
      const d = data[tooth][surface] || {};
      return [surface, d.condition_code ?? null, d.treatment_id ?? null, d.notes || ''];
    }),
  ])
);

const Odontogram = ({ patientId, currentBranch, onTreatmentLink, onDirtyChange }) => {
  const { print_lang, currentLanguage } = useLanguage();

  // Helper function to get condition display name based on language
  const getConditionName = (condition) => {
    if (!condition) return '';
    if (typeof condition === 'string') {
      // If it's a condition code, lookup from conditions array
      const found = conditions.find(c => c.code === condition || c.name === condition);
      if (found) {
        return currentLanguage === 'th' ? (found.name_th || found.name) : found.name;
      }
      return condition;
    }
    return currentLanguage === 'th' ? (condition.name_th || condition.name) : condition.name;
  };

  // Helper function to get surface name based on language
  const getSurfaceName = (surfaceKey) => {
    const surfaceNames = {
      O: { en: 'Occlusal/Incisal', th: 'ด้านบดเคี้ยว' },
      M: { en: 'Mesial', th: 'ด้านใกล้กลาง' },
      D: { en: 'Distal', th: 'ด้านไกลกลาง' },
      B: { en: 'Buccal/Facial', th: 'ด้านแก้ม' },
      L: { en: 'Lingual/Palatal', th: 'ด้านลิ้น' }
    };
    const surface = surfaceNames[surfaceKey];
    if (!surface) return surfaceKey;
    return currentLanguage === 'th' ? surface.th : surface.en;
  };

  // Data states
  const [toothData, setToothData] = useState({});
  // Fingerprint of the last chart the server confirmed. Everything the dentist
  // marks lives only in `toothData` until Save, and this screen can be
  // unmounted from the header above it (Clinical Record, Back) — so the parent
  // needs to know when that would throw work away.
  const [savedFingerprint, setSavedFingerprint] = useState(() => chartFingerprint({}));
  const [conditions, setConditions] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [history, setHistory] = useState([]);
  const [snapshots, setSnapshots] = useState([]);

  // UI states
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [selectedTooth, setSelectedTooth] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedSurface, setSelectedSurface] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Inline status banner for save success/failure, and the in-page reset
  // confirmation — no window.alert/confirm anywhere in this component.
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Modal states
  const [showHistory, setShowHistory] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showTreatmentLink, setShowTreatmentLink] = useState(false);
  const [showToothDetail, setShowToothDetail] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState('adult'); // 'adult', 'child', 'mixed'
  const [comparisonDate, setComparisonDate] = useState(null);
  const [comparisonData, setComparisonData] = useState({});

  // Adult teeth numbering (FDI World Dental Federation notation)
  const upperTeeth = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
  const lowerTeeth = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

  // Primary teeth (children)
  const upperPrimaryTeeth = [55, 54, 53, 52, 51, 61, 62, 63, 64, 65];
  const lowerPrimaryTeeth = [85, 84, 83, 82, 81, 71, 72, 73, 74, 75];

  // Surfaces
  const surfaces = ['O', 'M', 'D', 'B', 'L'];

  const fetchConditions = async () => {
    try {
      const response = await fetchWithAuth('/api/odontogram/conditions');
      const data = response.data || response || [];
      setConditions(data);
      if (data.length > 0) {
        setSelectedCondition(data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch conditions:', error);
    }
  };

  const fetchTreatments = async () => {
    try {
      const response = await fetchWithAuth('/api/treatments?status=active');
      const data = response.data || response || [];
      setTreatments(data);
    } catch (error) {
      console.error('Failed to fetch treatments:', error);
    }
  };

  const fetchOdontogram = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`/api/odontogram/patient/${patientId}`);
      const data = response.data || response || [];

      // Convert to nested structure: { toothNumber: { surface: conditionData } }
      const toothMap = {};
      data.forEach(item => {
        if (!toothMap[item.tooth_number]) {
          toothMap[item.tooth_number] = {};
        }
        const surface = item.surface || 'WHOLE';
        toothMap[item.tooth_number][surface] = {
          odonto_id: item.odonto_id,
          condition_code: item.condition_code,
          condition_name: item.condition_name,
          color: item.color,
          treatment_id: item.treatment_id,
          treatment_name: item.treatment_name,
          notes: item.notes,
          recorded_date: item.recorded_date
        };
      });
      setToothData(toothMap);
      // This is now the chart of record: Save, Reset and the initial load all
      // route through here, so each of them clears the unsaved-changes flag.
      setSavedFingerprint(chartFingerprint(toothMap));
    } catch (error) {
      console.error('Failed to fetch odontogram:', error);
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  const fetchSnapshots = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/odontogram/snapshots/${patientId}`);
      setSnapshots(response.data || response || []);
    } catch (error) {
      console.error('Failed to fetch snapshots:', error);
    }
  }, [patientId]);

  useEffect(() => {
    fetchConditions();
    fetchTreatments();
  }, []);

  useEffect(() => {
    if (patientId) {
      fetchOdontogram();
      fetchSnapshots();
    }
  }, [patientId, fetchOdontogram, fetchSnapshots]);

  // Tell the parent whether the chart holds marks that Save has not written
  // yet, so it can guard a navigation that would unmount this component.
  // `onDirtyChange` must be a stable reference (useCallback) in the parent.
  const isDirty = chartFingerprint(toothData) !== savedFingerprint;
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Once this chart is gone there is nothing left to lose; without this the
  // parent would keep a stale "unsaved" flag after the screen closed.
  useEffect(() => () => {
    if (onDirtyChange) onDirtyChange(false);
  }, [onDirtyChange]);

  const fetchHistory = async () => {
    try {
      const response = await fetchWithAuth(`/api/odontogram/history/${patientId}`);
      setHistory(response.data || response || []);
      setShowHistory(true);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const fetchComparisonData = async (date) => {
    try {
      const response = await fetchWithAuth(`/api/odontogram/patient/${patientId}?asOf=${date}`);
      const data = response.data || response || [];

      const toothMap = {};
      data.forEach(item => {
        if (!toothMap[item.tooth_number]) {
          toothMap[item.tooth_number] = {};
        }
        const surface = item.surface || 'WHOLE';
        toothMap[item.tooth_number][surface] = {
          condition_code: item.condition_code,
          color: item.color
        };
      });
      setComparisonData(toothMap);
      setComparisonDate(date);
      setShowComparison(true);
    } catch (error) {
      console.error('Failed to fetch comparison data:', error);
    }
  };

  const handleSurfaceClick = (toothNumber, surface) => {
    if (!selectedCondition) return;

    const toothStr = toothNumber.toString();

    setToothData(prev => {
      const tooth = prev[toothStr] || {};
      const current = tooth[surface];

      // If same condition on same surface, remove it
      if (current?.condition_code === selectedCondition.code) {
        const newTooth = { ...tooth };
        delete newTooth[surface];

        if (Object.keys(newTooth).length === 0) {
          const { [toothStr]: _, ...rest } = prev;
          return rest;
        }

        return { ...prev, [toothStr]: newTooth };
      }

      // Set new condition
      return {
        ...prev,
        [toothStr]: {
          ...tooth,
          [surface]: {
            condition_code: selectedCondition.code,
            condition_name: selectedCondition.name,
            color: selectedCondition.color,
            symbol: selectedCondition.symbol,
            treatment_id: selectedTreatment?.t_id || null,
            treatment_name: selectedTreatment?.name || null,
            isNew: true
          }
        }
      };
    });
  };

  const handleWholeToothClick = (toothNumber) => {
    if (!selectedCondition) return;

    const toothStr = toothNumber.toString();

    // Special handling for "missing" or "implant" - applies to whole tooth
    if (['missing', 'implant', 'crown', 'bridge'].includes(selectedCondition.code)) {
      setToothData(prev => {
        const current = prev[toothStr]?.['WHOLE'];

        if (current?.condition_code === selectedCondition.code) {
          const { [toothStr]: _, ...rest } = prev;
          return rest;
        }

        return {
          ...prev,
          [toothStr]: {
            WHOLE: {
              condition_code: selectedCondition.code,
              condition_name: selectedCondition.name,
              color: selectedCondition.color,
              symbol: selectedCondition.symbol,
              treatment_id: selectedTreatment?.t_id || null,
              treatment_name: selectedTreatment?.name || null,
              isNew: true
            }
          }
        };
      });
    } else {
      // Show tooth detail for surface selection
      setSelectedTooth(toothNumber);
      setShowToothDetail(true);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setStatusMessage({ type: '', text: '' });
    try {
      const teeth = [];

      Object.entries(toothData).forEach(([tooth_number, surfaceData]) => {
        Object.entries(surfaceData).forEach(([surface, data]) => {
          teeth.push({
            tooth_number: parseInt(tooth_number),
            surface: surface === 'WHOLE' ? null : surface,
            condition_code: data.condition_code,
            condition_name: data.condition_name,
            color: data.color,
            treatment_id: data.treatment_id,
            notes: data.notes || ''
          });
        });
      });

      await fetchWithAuth('/api/odontogram/bulk-update', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: patientId,
          teeth,
          branch_id: currentBranch?.branch_id
        })
      });

      setStatusMessage({ type: 'success', text: print_lang('saved_success', 'Saved successfully') });
      fetchOdontogram(); // Refresh data
    } catch (error) {
      console.error('Failed to save odontogram:', error);
      // Prefer the translated string — fetchWithAuth always populates
      // error.message with the server's raw (often English) text, which may
      // not be readable by a Thai-speaking dentist. It's kept only as a
      // fallback in case the translation itself is ever unavailable.
      setStatusMessage({
        type: 'error',
        text: print_lang('save_failed', 'Save failed') || error.message
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setShowResetConfirm(true);
  };

  const confirmReset = () => {
    setShowResetConfirm(false);
    fetchOdontogram();
  };

  const getToothSurfaces = (toothNumber) => {
    const toothStr = toothNumber.toString();
    const data = toothData[toothStr] || {};

    // Convert to format expected by ToothSVG
    const surfaceMap = {};
    Object.entries(data).forEach(([surface, condData]) => {
      if (surface !== 'WHOLE') {
        surfaceMap[surface] = {
          color: condData.color,
          symbol: condData.symbol
        };
      }
    });
    return surfaceMap;
  };

  const isToothMissing = (toothNumber) => {
    const data = toothData[toothNumber.toString()]?.['WHOLE'];
    return data?.condition_code === 'missing';
  };

  const isToothImplant = (toothNumber) => {
    const data = toothData[toothNumber.toString()]?.['WHOLE'];
    return data?.condition_code === 'implant';
  };

  const getToothWholeColor = (toothNumber) => {
    return toothData[toothNumber.toString()]?.['WHOLE']?.color;
  };

  const renderTooth = (toothNumber, isPrimary = false) => {
    const size = isPrimary ? 40 : 50;

    if (isToothMissing(toothNumber)) {
      return (
        <div
          key={toothNumber}
          className="cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => handleWholeToothClick(toothNumber)}
        >
          <MissingTooth toothNumber={toothNumber} size={size} isPrimary={isPrimary} />
        </div>
      );
    }

    if (isToothImplant(toothNumber)) {
      return (
        <div
          key={toothNumber}
          className="cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => handleWholeToothClick(toothNumber)}
        >
          <ImplantTooth
            toothNumber={toothNumber}
            size={size}
            isPrimary={isPrimary}
            color={getToothWholeColor(toothNumber)}
          />
        </div>
      );
    }

    return (
      <div key={toothNumber} onClick={() => handleWholeToothClick(toothNumber)}>
        <ToothSVG
          toothNumber={toothNumber}
          surfaces={getToothSurfaces(toothNumber)}
          onSurfaceClick={handleSurfaceClick}
          size={size}
          isPrimary={isPrimary}
        />
      </div>
    );
  };

  if (!patientId) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <Smile size={48} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">{print_lang('select_patient_odontogram', 'Select a patient to view odontogram')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Smile size={24} />
          {print_lang('odontogram', 'Odontogram')}
        </h2>
        <div className="flex flex-wrap gap-2">
          {/* View Mode Toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setViewMode('adult')}
              className={`px-3 py-1.5 text-sm font-medium ${viewMode === 'adult' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {print_lang('adult', 'Adult')}
            </button>
            <button
              onClick={() => setViewMode('child')}
              className={`px-3 py-1.5 text-sm font-medium border-l ${viewMode === 'child' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {print_lang('child', 'Child')}
            </button>
            <button
              onClick={() => setViewMode('mixed')}
              className={`px-3 py-1.5 text-sm font-medium border-l ${viewMode === 'mixed' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {print_lang('mixed', 'Mixed')}
            </button>
          </div>

          <button onClick={fetchHistory} className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <History size={18} />
            {print_lang('history', 'History')}
          </button>

          {snapshots.length > 0 && (
            <button
              onClick={() => fetchComparisonData(snapshots[0].snapshot_date)}
              className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Layers size={18} />
              {print_lang('compare', 'Compare')}
            </button>
          )}

          <button onClick={handleReset} className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <RotateCcw size={18} />
            {print_lang('reset', 'Reset')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={18} />
            {isSaving ? print_lang('saving', 'Saving...') : print_lang('save', 'Save')}
          </button>
        </div>
      </div>

      {/* Save success/failure banner — renders inline in the tab that fired
          the save/reset action, so it stays visible whether Odontogram is
          mounted inside PatientRecord's Odontogram tab or WaitingList's
          treatment view. No window.alert anywhere in this component. */}
      {statusMessage.text && (
        <div
          className={`px-3 py-2 rounded-lg flex justify-between items-center border ${
            statusMessage.type === 'success'
              ? 'bg-green-50 border-green-300 text-green-700'
              : 'bg-red-50 border-red-300 text-red-700'
          }`}
        >
          <span className="text-sm">{statusMessage.text}</span>
          <button
            className={`min-w-[44px] min-h-[44px] flex items-center justify-center ${
              statusMessage.type === 'success' ? 'text-green-700' : 'text-red-700'
            }`}
            onClick={() => setStatusMessage({ type: '', text: '' })}
          >
            &times;
          </button>
        </div>
      )}

      {/* Condition Palette */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          {print_lang('select_condition', 'Select Condition')}
        </h3>
        <div className="flex flex-wrap gap-2">
          {conditions.map(condition => (
            <button
              key={condition.condition_id}
              onClick={() => setSelectedCondition(condition)}
              className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-2 ${
                selectedCondition?.condition_id === condition.condition_id
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              style={{ backgroundColor: condition.color }}
            >
              {condition.symbol && <span className="font-bold">{condition.symbol}</span>}
              <span>{condition.code} - {getConditionName(condition)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Treatment Link (Optional) */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <LinkIcon size={16} />
            {print_lang('link_treatment', 'Link to Treatment')} ({print_lang('optional', 'Optional')})
          </h3>
          {selectedTreatment && (
            <button
              onClick={() => setSelectedTreatment(null)}
              className="text-sm text-red-500 hover:text-red-700"
            >
              {print_lang('clear', 'Clear')}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
          {treatments.slice(0, 20).map(treatment => (
            <button
              key={treatment.t_id}
              onClick={() => setSelectedTreatment(treatment)}
              className={`px-2 py-1 rounded text-xs border transition-all ${
                selectedTreatment?.t_id === treatment.t_id
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {currentLanguage === 'th' ? (treatment.name_th || treatment.name) : treatment.name}
              {treatment.price > 0 && (
                <span className="ml-1 text-gray-400">{treatment.price.toLocaleString()}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Odontogram Chart */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-4 overflow-x-auto">
          {/* Adult Teeth - Upper */}
          {(viewMode === 'adult' || viewMode === 'mixed') && (
            <div className="mb-6">
              <div className="text-center text-sm text-gray-500 mb-2">
                {print_lang('upper_right', 'Upper Right')} - {print_lang('upper_left', 'Upper Left')}
              </div>
              <div className="flex justify-center gap-1 flex-wrap">
                {upperTeeth.map(tooth => renderTooth(tooth))}
              </div>
            </div>
          )}

          {/* Primary Teeth - Upper */}
          {(viewMode === 'child' || viewMode === 'mixed') && (
            <div className="mb-6 bg-blue-50 p-4 rounded-lg">
              <div className="text-center text-xs text-gray-500 mb-2">
                {print_lang('primary_teeth', 'Primary Teeth')} ({print_lang('upper', 'Upper')})
              </div>
              <div className="flex justify-center gap-1 flex-wrap">
                {upperPrimaryTeeth.map(tooth => renderTooth(tooth, true))}
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="border-t-2 border-gray-300 my-4 relative">
            <span className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-gray-400 text-sm">
              {print_lang('midline', 'Midline')}
            </span>
          </div>

          {/* Primary Teeth - Lower */}
          {(viewMode === 'child' || viewMode === 'mixed') && (
            <div className="mb-6 bg-blue-50 p-4 rounded-lg">
              <div className="text-center text-xs text-gray-500 mb-2">
                {print_lang('primary_teeth', 'Primary Teeth')} ({print_lang('lower', 'Lower')})
              </div>
              <div className="flex justify-center gap-1 flex-wrap">
                {lowerPrimaryTeeth.map(tooth => renderTooth(tooth, true))}
              </div>
            </div>
          )}

          {/* Adult Teeth - Lower */}
          {(viewMode === 'adult' || viewMode === 'mixed') && (
            <div>
              <div className="flex justify-center gap-1 flex-wrap">
                {lowerTeeth.map(tooth => renderTooth(tooth))}
              </div>
              <div className="text-center text-sm text-gray-500 mt-2">
                {print_lang('lower_right', 'Lower Right')} - {print_lang('lower_left', 'Lower Left')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">{print_lang('legend', 'Legend')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {conditions.map(condition => (
            <div key={condition.condition_id} className="flex items-center gap-2">
              <div
                className="w-6 h-6 rounded border border-gray-300 flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: condition.color }}
              >
                {condition.symbol}
              </div>
              <span className="text-sm">
                <strong>{condition.code}</strong> - {getConditionName(condition)}
              </span>
            </div>
          ))}
        </div>

        {/* Surface Legend */}
        <div className="mt-4 pt-4 border-t">
          <h4 className="text-sm font-medium text-gray-700 mb-2">{print_lang('surfaces', 'Surfaces')}</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
            {surfaces.map((key) => (
              <div key={key} className="flex items-center gap-1">
                <span className="font-mono font-bold w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                  {key}
                </span>
                <span>{getSurfaceName(key)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reset confirmation — in-page, no window.confirm. A click outside
          the box cancels, same as the other modals in this component. */}
      {showResetConfirm && (
        <div className="modal-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">
              {print_lang('confirm_reset', 'Are you sure you want to reset?')}
            </h3>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowResetConfirm(false)}
                className="min-h-[44px] px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {print_lang('cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={confirmReset}
                className="min-h-[44px] px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {print_lang('reset', 'Reset')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tooth Detail Modal */}
      {showToothDetail && selectedTooth && (
        <div className="modal-overlay" onClick={() => setShowToothDetail(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {print_lang('tooth', 'Tooth')} #{selectedTooth}
              </h2>
              <button onClick={() => setShowToothDetail(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <div className="flex justify-center mb-6">
              <ToothSVG
                toothNumber={selectedTooth}
                surfaces={getToothSurfaces(selectedTooth)}
                onSurfaceClick={handleSurfaceClick}
                selectedSurface={selectedSurface}
                size={120}
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {print_lang('click_surface', 'Click on a surface to mark, or select below')}:
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {surfaces.map(surface => {
                    const surfaceData = toothData[selectedTooth.toString()]?.[surface];
                    return (
                      <button
                        key={surface}
                        onClick={() => handleSurfaceClick(selectedTooth, surface)}
                        className={`p-3 rounded-lg border-2 text-center ${
                          surfaceData
                            ? 'border-gray-400'
                            : 'border-gray-200 hover:border-blue-400'
                        }`}
                        style={surfaceData ? { backgroundColor: surfaceData.color } : {}}
                      >
                        <div className="font-bold">{surface}</div>
                        <div className="text-xs text-gray-500">{getSurfaceName(surface)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Current conditions on this tooth */}
              {Object.keys(toothData[selectedTooth.toString()] || {}).length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {print_lang('current_conditions', 'Current Conditions')}:
                  </label>
                  <div className="space-y-1">
                    {Object.entries(toothData[selectedTooth.toString()] || {}).map(([surface, data]) => (
                      <div
                        key={surface}
                        className="flex items-center justify-between p-2 rounded"
                        style={{ backgroundColor: data.color + '20' }}
                      >
                        <span>
                          <strong>{surface}</strong>: {getConditionName(data.condition_code) || data.condition_name}
                          {data.treatment_name && (
                            <span className="text-gray-500 ml-2">- {data.treatment_name}</span>
                          )}
                        </span>
                        <button
                          onClick={() => {
                            setToothData(prev => {
                              const newTooth = { ...prev[selectedTooth.toString()] };
                              delete newTooth[surface];
                              if (Object.keys(newTooth).length === 0) {
                                const { [selectedTooth.toString()]: _, ...rest } = prev;
                                return rest;
                              }
                              return { ...prev, [selectedTooth.toString()]: newTooth };
                            });
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowToothDetail(false)}
                className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {print_lang('close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <History size={24} />
                {print_lang('odontogram_history', 'Odontogram History')}
              </h2>
              <button onClick={() => setShowHistory(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            {history.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{print_lang('no_history', 'No history found')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">{print_lang('date', 'Date')}</th>
                      <th className="text-left p-3">{print_lang('tooth', 'Tooth')}</th>
                      <th className="text-left p-3">{print_lang('surface', 'Surface')}</th>
                      <th className="text-left p-3">{print_lang('previous', 'Previous')}</th>
                      <th className="text-left p-3">{print_lang('new', 'New')}</th>
                      <th className="text-left p-3">{print_lang('changed_by', 'Changed By')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-3 whitespace-nowrap">
                          {new Date(item.changed_at).toLocaleString('th-TH')}
                        </td>
                        <td className="p-3 font-mono font-bold">{item.tooth_number}</td>
                        <td className="p-3">{item.surface || '-'}</td>
                        <td className="p-3">
                          {item.previous_condition && (
                            <span className="px-2 py-1 rounded text-sm bg-gray-100">
                              {item.previous_condition}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 rounded text-sm bg-blue-100">
                            {item.new_condition}
                          </span>
                        </td>
                        <td className="p-3">{item.changed_by_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comparison Modal */}
      {showComparison && (
        <div className="modal-overlay" onClick={() => setShowComparison(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Layers size={24} />
                {print_lang('compare_odontogram', 'Compare Odontogram')}
              </h2>
              <button onClick={() => setShowComparison(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            {/* Snapshot Selector */}
            <div className="mb-6 flex items-center gap-4">
              <Calendar size={20} className="text-gray-500" />
              <select
                value={comparisonDate || ''}
                onChange={(e) => fetchComparisonData(e.target.value)}
                className="border rounded px-3 py-2"
              >
                {snapshots.map(snapshot => (
                  <option key={snapshot.snapshot_date} value={snapshot.snapshot_date}>
                    {formatDate(snapshot.snapshot_date)}
                  </option>
                ))}
              </select>
              <span className="text-gray-500">vs</span>
              <span className="font-medium">{print_lang('current', 'Current')}</span>
            </div>

            <div className="grid grid-cols-2 gap-8">
              {/* Past */}
              <div>
                <h3 className="text-lg font-medium text-center mb-4 text-gray-600">
                  {formatDate(comparisonDate)} ({print_lang('past', 'Past')})
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-center gap-1 flex-wrap mb-4">
                    {upperTeeth.map(tooth => {
                      const data = comparisonData[tooth.toString()];
                      return (
                        <div
                          key={tooth}
                          className="w-8 h-8 rounded border flex items-center justify-center text-xs font-mono"
                          style={{ backgroundColor: data?.WHOLE?.color || '#fff' }}
                        >
                          {tooth}
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t my-2"></div>
                  <div className="flex justify-center gap-1 flex-wrap">
                    {lowerTeeth.map(tooth => {
                      const data = comparisonData[tooth.toString()];
                      return (
                        <div
                          key={tooth}
                          className="w-8 h-8 rounded border flex items-center justify-center text-xs font-mono"
                          style={{ backgroundColor: data?.WHOLE?.color || '#fff' }}
                        >
                          {tooth}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Current */}
              <div>
                <h3 className="text-lg font-medium text-center mb-4 text-blue-600">
                  {print_lang('today', 'Today')} ({print_lang('current', 'Current')})
                </h3>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-center gap-1 flex-wrap mb-4">
                    {upperTeeth.map(tooth => {
                      const data = toothData[tooth.toString()];
                      const wholeColor = data?.WHOLE?.color;
                      return (
                        <div
                          key={tooth}
                          className="w-8 h-8 rounded border flex items-center justify-center text-xs font-mono"
                          style={{ backgroundColor: wholeColor || '#fff' }}
                        >
                          {tooth}
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t my-2"></div>
                  <div className="flex justify-center gap-1 flex-wrap">
                    {lowerTeeth.map(tooth => {
                      const data = toothData[tooth.toString()];
                      const wholeColor = data?.WHOLE?.color;
                      return (
                        <div
                          key={tooth}
                          className="w-8 h-8 rounded border flex items-center justify-center text-xs font-mono"
                          style={{ backgroundColor: wholeColor || '#fff' }}
                        >
                          {tooth}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Changes Summary */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">{print_lang('changes_summary', 'Changes Summary')}</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center p-3 bg-green-100 rounded">
                  <div className="text-2xl font-bold text-green-600">
                    {Object.keys(toothData).filter(t => !comparisonData[t]).length}
                  </div>
                  <div>{print_lang('new_conditions', 'New Conditions')}</div>
                </div>
                <div className="text-center p-3 bg-blue-100 rounded">
                  <div className="text-2xl font-bold text-blue-600">
                    {Object.keys(toothData).filter(t => comparisonData[t]).length}
                  </div>
                  <div>{print_lang('updated', 'Updated')}</div>
                </div>
                <div className="text-center p-3 bg-gray-100 rounded">
                  <div className="text-2xl font-bold text-gray-600">
                    {Object.keys(comparisonData).filter(t => !toothData[t]).length}
                  </div>
                  <div>{print_lang('resolved', 'Resolved')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Odontogram;
